import React, { ReactNode, useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

import { getMixpanelToken } from 'utils/getMixpanelToken';
import { PLAID_METADATA } from 'v3/constants/plaidMetadata';
import { isProduction } from 'v3/utils/checkEnv';

export const MixpanelContext = React.createContext<any>({});
const MIXPANEL_TOKEN = getMixpanelToken();

interface stepTypes {
  [key: number]: string;
}

const steps: stepTypes = {
  1: 'Start Deal',
  2: 'Review Offer',
  3: 'Sign Deal',
};

type RegisterContractSuccessPayload = {
  firstName: string;
  middleName: string;
  lastName: string;
  group: {
    id: number;
  };
  contractFlowGroupValueWithFee: number;
};

type RegisterReviewOfferProps = {
  totalCost: number;
  contractDuration: number;
  monthlyInstallment: number;
  halfPaymentSelected: boolean;
};

type CalculateDeal = {
  vehiclePrice: number;
  creditScore: number;
  loanTerms: number;
  downPayment: string;
  wantCar: string;
};

type StartDeal = {
  firstName: string;
  lastName: string;
  referralCode: string;
  sex: string;
  email: string;
  clubId: string;
  userPhone: string;
  zipCode: string;
  state: string;
  city: string;
  isSsnProvided: boolean;
};

type ReviewOffer = {
  totalCost: number;
  contractDuration: number;
  monthlyInstallment: number;
  downPayment: number;
  halfPaymentSelected: boolean;
};

type RecalculateProps = {
  newValue: number;
  months: number;
  downPayment: number;
  paymentOption: string;
};

type ExitStatus = keyof typeof PLAID_METADATA;

export const MixpanelProvider = ({ children }: { children: ReactNode }) => {
  const { leadData, currentStep } = useSelector(
    (state: RootState) => state.lead
  );
  const [initialized, setInitialized] = useState(false);
  const { email } = leadData;

  const identify = (email: string) => {
    // Identify is called to identify him if he ever comes back.
    mixpanel.identify(email);
  };

  const updateLastStep = (newLastStep: string) => {
    mixpanel.people.set('Last Step on Contract', newLastStep);
  };

  // Update the current contract step
  useEffect(() => {
    if (currentStep) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      updateLastStep(steps[currentStep + 1]);
    }
  }, [currentStep]);

  // Initialize Mixpanel along with application
  useEffect(() => {
    (function initializeMixpanel() {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: !isProduction(),
        ignore_dnt: true,
      });

      setInitialized(true);
    })();
  }, []);

  // Identify user if he is already on a contract
  useEffect(() => {
    if (email) {
      identify(email);
    }
  }, [email]);

  const alias = (email: string) => {
    // Alias is called on the first time we are introducing a new user to mixpanel
    mixpanel.alias(email);
  };

  const registerClubId = (clubId: number) => {
    mixpanel.people.set('Club ID', clubId);
  };

  const registerBasicInfo = ({
    firstName,
    lastName,
    referralCode,
    sex,
    email,
    clubId,
    userPhone,
  }: any) => {
    alias(email);
    registerClubId(clubId);

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    mixpanel.people.set('$name', `${firstName} ${lastName}`);
    mixpanel.people.set('$email', email);

    mixpanel.people.set('First Name', firstName);
    mixpanel.people.set('Last Name', lastName);
    mixpanel.people.set('Phone Number', userPhone);
    mixpanel.people.set('Referral Code', referralCode);
    mixpanel.people.set('Gender', sex);
  };

  const registerPersonalInfo = ({
    zipCode,
    houseOwnership,
    housingExpenses,
    yearsLivingHere,
    state,
    city,
  }: any) => {
    mixpanel.people.set('Zip Code', zipCode);
    mixpanel.people.set('State', state);
    mixpanel.people.set('Town/City', city);
    mixpanel.people.set('House Ownership', houseOwnership);
    mixpanel.people.set('Housing Expenses', housingExpenses);
    mixpanel.people.set('Years Living Here', yearsLivingHere);
    mixpanel.people.set('User on Waiting List', false);
  };

  const registerWaitingList = () => {
    mixpanel.people.set('User on Waiting List', true);
  };

  const registerFundsNIncome = ({
    occupation,
    timeWorking,
    yearlyIncome,
    otherIncome,
    otherIncomeSource,
    wentBankrupt,
    bankruptcyType,
    wasItDischarged,
    maritalStatus,
    surrendedOrRepossessedItem,
  }: any) => {
    mixpanel.people.set('Occupation', occupation);
    mixpanel.people.set('Time Working Here', timeWorking);
    mixpanel.people.set('Yearly Income', yearlyIncome);
    mixpanel.people.set('Other Income', otherIncome);
    mixpanel.people.set('Other Income Source', otherIncomeSource);
    mixpanel.people.set('Went Bankrupt', wentBankrupt);
    mixpanel.people.set('Bankruptcy Type', bankruptcyType);
    mixpanel.people.set('Was it Discharged', wasItDischarged);
    mixpanel.people.set('Other Income Source', otherIncomeSource);
    mixpanel.people.set('Marital Status', maritalStatus);
    mixpanel.people.set(
      'Surrended/Repossessed Item',
      surrendedOrRepossessedItem
    );
  };

  const registerReviewOffer = ({
    totalCost,
    contractDuration,
    monthlyInstallment,
    halfPaymentSelected,
  }: RegisterReviewOfferProps) => {
    mixpanel.people.set('Contract Total Value', totalCost);
    mixpanel.people.set('Contract Duration (months)', contractDuration);
    mixpanel.people.set('Contract Installments Value', monthlyInstallment);
    mixpanel.people.set('Half Payment Selected', halfPaymentSelected);
  };

  const registerPlaidClick = () => {
    mixpanel.people.set('Payment Info - Clicked Plaid', true);
  };

  const registerPlaidSuccess = () => {
    mixpanel.people.set('Payment Info - Success on Plaid', true);
  };

  const registerContractFlowSuccess = ({
    firstName,
    middleName,
    lastName,
    group,
    contractFlowGroupValueWithFee,
  }: RegisterContractSuccessPayload) => {
    mixpanel.track('Contract Flow Success', {
      Name: `${firstName} ${middleName} ${lastName}`,
      Email: email,
      'Club ID': group.id,
      'Total Value With Fee': contractFlowGroupValueWithFee,
    });
  };

  const fireAccessBasicInfo = () => {
    mixpanel.track('Access Basic Info');
  };

  const fireAccessPersonalInfo = () => {
    mixpanel.track('Access Personal Info');
  };

  const fireUserOnWaitingList = () => {
    mixpanel.track('Waiting List');
  };

  const fireAccessFundsNIncome = () => {
    mixpanel.track('Access Funds & Income');
  };

  const fireAccessReviewOffer = () => {
    mixpanel.track('Access Review Offer');
  };

  const fireAccessSignContract = () => {
    mixpanel.track('Access Sign Contract');
  };

  const firePlaidSuccess = () => {
    mixpanel.track('Payment Info - Success on Plaid');
  };

  const fireCreditCardSuccess = () => {
    mixpanel.track('Payment Info - Success on Credit Card');
  };

  const fireManualVerification = () => {
    mixpanel.track('Plaid Manual Verification Attempt');
  };

  const firePlaidNextStep = (step: string) => {
    mixpanel.track('Plaid Link Step', {
      'Current Step': step,
    });
  };

  const firePlaidOnExit = (status: ExitStatus) => {
    mixpanel.track('Plaid Exit Incomplete', {
      Status: status,
      Description: PLAID_METADATA[status],
    });
  };

  const firePlaidError = (error: string) => {
    mixpanel.track('Plaid Error', {
      Error: error,
    });
  };

  const plaidHelpClick = () => {
    mixpanel.track('Click on Plaid - Learn More');
  };

  const videoClickReviewOffer = () => {
    mixpanel.track('Click on Review Offer video');
  };

  const fireRecalculate = (newValue: number, months: number) => {
    mixpanel.track('Recalculate Club value', {
      Value: newValue,
      Months: months,
    });
  };

  const fireCalculateDeal = ({
    vehiclePrice,
    creditScore,
    loanTerms,
    downPayment,
    wantCar,
  }: CalculateDeal) => {
    mixpanel.track('Calculate Deal', {
      'Vehicle Price': vehiclePrice,
      'Credit Score': creditScore,
      'Loan Term': loanTerms,
      'Down Payment': downPayment,
      'When Do You Want Your Car': wantCar,
    });
  };

  const registerStartDeal = ({
    firstName,
    lastName,
    referralCode,
    sex,
    email,
    clubId,
    userPhone,
    zipCode,
    state,
    city,
    isSsnProvided,
  }: StartDeal) => {
    alias(email);
    registerClubId(Number(clubId));
    mixpanel.people.set('$name', `${firstName} ${lastName}`);
    mixpanel.people.set('$email', email);

    mixpanel.people.set('First Name', firstName);
    mixpanel.people.set('Last Name', lastName);
    mixpanel.people.set('Phone Number', userPhone);
    mixpanel.people.set('Referral Code', referralCode);
    mixpanel.people.set('Gender', sex);
    mixpanel.people.set('Zip Code', zipCode);
    mixpanel.people.set('State', state);
    mixpanel.people.set('Town/City', city);
    mixpanel.people.set('User on Waiting List', false);
    mixpanel.people.set('SSN Provided', isSsnProvided);
  };

  const registerOffer = ({
    totalCost,
    contractDuration,
    monthlyInstallment,
    downPayment,
    halfPaymentSelected,
  }: ReviewOffer) => {
    mixpanel.people.set('Contract Total Value', totalCost);
    mixpanel.people.set('Contract Duration (months)', contractDuration);
    mixpanel.people.set('Contract Installments Value', monthlyInstallment);
    mixpanel.people.set('Down Payment', downPayment);
    mixpanel.people.set('Half Payment Selected', halfPaymentSelected);
  };

  const fireRecalculateOffer = ({
    newValue,
    months,
    downPayment,
    paymentOption,
  }: RecalculateProps) => {
    mixpanel.track('Recalculate Deal', {
      Value: newValue,
      Months: months,
      'Down Payment': downPayment,
      'Payment Option': paymentOption,
    });
  };

  const fireNewsletterSubscription = (email: string) =>
    mixpanel.track('Newsletter Subscription', {
      Email: email,
    });

  const fireAccessStartDeal = () => {
    mixpanel.track('Access Start Deal');
  };

  const fireAccessSignDeal = () => {
    mixpanel.track('Access Sign Deal');
  };

  const firePromotionalModal = () => {
    mixpanel.track('Promotional modal (50$ off) displayed');
  };

  const fireDefaultExitModal = () => {
    mixpanel.track('Default warning modal displayed');
  };

  const fireAccessWelcome = () => {
    mixpanel.track('Access Welcome page');
  };

  return (
    <MixpanelContext.Provider
      value={{
        // Users
        steps,
        updateLastStep,
        registerBasicInfo,
        registerPersonalInfo,
        registerFundsNIncome,
        registerReviewOffer,
        registerPlaidClick,
        // Conversion Funnel
        fireAccessBasicInfo,
        fireAccessPersonalInfo,
        fireAccessFundsNIncome,
        fireAccessSignContract,
        fireAccessWelcome,

        fireRecalculate,
        fireCreditCardSuccess,
        // Events
        plaidHelpClick,
        videoClickReviewOffer,

        // v3
        fireCalculateDeal,
        fireNewsletterSubscription,
        registerClubId,
        registerPlaidSuccess,
        registerContractFlowSuccess,
        fireAccessStartDeal,
        fireAccessReviewOffer,
        fireAccessSignDeal,
        registerStartDeal,
        registerOffer,
        fireUserOnWaitingList,
        fireRecalculateOffer,
        firePlaidSuccess,
        registerWaitingList,
        firePlaidOnExit,
        fireManualVerification,
        firePlaidNextStep,
        firePlaidError,
        fireDefaultExitModal,
        firePromotionalModal,
        initialized,
        alias,
        identify,
      }}
    >
      {children}
    </MixpanelContext.Provider>
  );
};
